import React from 'react'
import PropTypes from 'prop-types'
import { Button, SearchInput } from 'components/UIElements'
import { browserHistory } from 'utils/navigation'
import ActiveDataSection from './ActiveData'
import DeleteParticipant from './DeleteParticipant'
import CohortList from './CohortList'
import FilterTagsPopup from './Filters/FilterTagsPopup'
import ParticipantList from './ParticipantList'
import '../stylesheets/Participants.scss'
import { isPtpInUncompletedConsentBlockList } from '../../../utils/participantValidation'
import StickyHeader from '../../../../../../../../../components/StickyHeader'
import { calculateOffsets } from '../../../../../../../../../utils/misc'
import { MODAL_CLASSES_MAP } from '../../../../../../../../../utils/constants'
import CaregiversList from '../../Caregiver/CaregiversPage/components/CaregiversList'
import CaregiversAssignmentContainer from '../../Caregiver/CaregiversAssignment/containers/CaregiversAssignmentContainer'
import { getInLeafSiteFromRow } from '../../../utils/pariticipantUtils'

class ParticipantsPage extends React.Component {
  constructor(props) {
    super(props)
    const { page } = this.props
    this.state = {
      page: page || 'participants',
      offsets: null,
      hasOrphans: false,
      dataSectionSelected: null,
    }
    this.onDataSectionSelect = this.onDataSectionSelect.bind(this)
  }

  componentDidMount() {
    const { canSeeOrphans, participantList, clearFilters, resetCheckedPtps, leafSites } = this.props
    resetCheckedPtps()
    clearFilters()
    this.calculateAndSetOffsets()
    // Only check for orphans if there are leaf sites in the current study version
    // If there are no leaf sites (e.g., no Uat sites), participants shouldn't be marked as missing a site
    const hasLeafSites = leafSites && leafSites.length > 0
    const containsOrphans = hasLeafSites && participantList.some(row => {
      return !getInLeafSiteFromRow(row)
    })

    /* We know there are orphans in the participant tables, if the current user is a Study Coordinator
     * or Investigator and if the leafSites array does not include the baseSiteID and some participants
     * are still in the baseSiteID (containsBaseSite boolean)
     */
    if (canSeeOrphans && containsOrphans) {
      this.setState({ hasOrphans: true }, () => this.onSetOrphanedParticipants())
    } else {
      this.onSetOrphanedParticipants()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      filterProps: {
        filters: { checkbox: prevCheckbox },
      },
      leafSites: prevLeafSites,
      participantList: prevParticipantList,
    } = prevProps
    const {
      filterProps: {
        filters: { checkbox },
      },
      canSeeOrphans,
      leafSites,
      participantList,
    } = this.props
    const { page, hasOrphans } = this.state
    if (prevState.page !== page) this.calculateAndSetOffsets()
    if (prevCheckbox && !checkbox) this.onDataSectionSelect(null)

    // Recalculate orphans if leafSites or participantList changes
    // Check if arrays actually changed by comparing lengths and references
    const leafSitesChanged = prevLeafSites !== leafSites &&
      (prevLeafSites?.length !== leafSites?.length ||
        (prevLeafSites && leafSites && prevLeafSites.length > 0 && leafSites.length > 0 && prevLeafSites[0] !== leafSites[0]))
    const participantListChanged = prevParticipantList !== participantList &&
      prevParticipantList?.length !== participantList?.length

    if (canSeeOrphans && (leafSitesChanged || participantListChanged)) {
      const hasLeafSites = leafSites && leafSites.length > 0
      const containsOrphans = hasLeafSites && participantList.some(row => {
        return !getInLeafSiteFromRow(row)
      })
      // Only update state if it actually changed
      if (containsOrphans && !hasOrphans) {
        this.setState({ hasOrphans: true }, () => this.onSetOrphanedParticipants())
      } else if (!containsOrphans && hasOrphans) {
        this.setState({ hasOrphans: false }, () => this.onSetOrphanedParticipants())
      }
    }
  }

  calculateAndSetOffsets = () => {
    this.setState({ offsets: calculateOffsets('sticky') })
  }

  onSetOrphanedParticipants = () => {
    const { onSetOrphanedParticipants } = this.props
    const { hasOrphans } = this.state
    if (hasOrphans) return onSetOrphanedParticipants(true)
    return onSetOrphanedParticipants(false)
  }

  togglePage = _page => {
    const { page } = this.state
    if (_page !== page) this.setState({ page: _page })
  }

  _validateCompletedBlockedConsents = () => {
    const { checkedPtps, studyID, unCompleteBlockedConsents, ptpInBlockedConsentAlert, studyVersionID } = this.props

    if (isPtpInUncompletedConsentBlockList(Object.keys(checkedPtps), Object.values(unCompleteBlockedConsents))) {
      ptpInBlockedConsentAlert(checkedPtps, unCompleteBlockedConsents)
    } else {
      browserHistory.push(`/studies/${studyID}/${studyVersionID}/participants/move`)
    }
  }

  renderMovePtpButton = () => {
    const { checkedPtps } = this.props
    const checkedPtpCount = Object.keys(checkedPtps).length
    const anyChecked = checkedPtpCount > 0
    return (
      <div className='move-ptp-btn'>
        {anyChecked && (
          <Button
            id='move-participant-button'
            content='Move to...'
            grey
            onClick={this._validateCompletedBlockedConsents}
          />
        )}
      </div>
    )
  }

  clearStateFilters() {
    this.setState({
      selected: null,
    })
  }

  onDataSectionSelect(value) {
    this.setState({
      dataSectionSelected: value,
    })
  }

  renderPtpButtons = () => {
    const {
      canAddCohort,
      canAddPtp,
      canDelete,
      checkedPtps,
      noTracksFeature,
      isCaseManager,
      isClinOrSiteCoordinator,
      studyID,
      openModal,
      studyLock,
      studyVersionID,
    } = this.props
    const checkedPtpCount = Object.keys(checkedPtps).length
    const anyChecked = checkedPtpCount > 0
    const plural = checkedPtpCount > 1
    return (
      <>
        <div className='action-buttons-list'>
          {anyChecked && !isCaseManager && canDelete && !isClinOrSiteCoordinator && (
            <Button
              grey
              content={`Delete Participant${plural ? 's' : ''}`}
              onClick={() => {
                openModal({
                  content: <DeleteParticipant {...this.props} plural={plural} />,
                  noButtons: true,
                  className: MODAL_CLASSES_MAP.deleteParticipant,
                })
              }}
              id='delete-participant-button'
              disabled={studyLock}
            />
          )}
          {anyChecked && canAddCohort && !noTracksFeature && (
            <Button
              grey
              content='Add Track'
              onClick={() => browserHistory.push(`/studies/${studyID}/${studyVersionID}/participants/tracks/create`)}
              id='create-cohort-button'
              disabled={studyLock}
            />
          )}
          {canAddPtp && (
            <Button
              content='Add Participant'
              disabled={studyLock}
              onClick={() => browserHistory.push(`/studies/${studyID}/${studyVersionID}/participants/create`)}
              id='create-participant-button'
            />
          )}
        </div>
        <div className='filters-container'>
          <SearchInput />
        </div>
      </>
    )
  }

  render() {
    const {
      canAddCohort,
      canAddPtp,
      canMovePtp,
      canWrite,
      cohortList,
      disableTrackCreation,
      downloadData,
      noTracksFeature,
      siteID,
      studyID,
      studyLock,
      isReadOnlyState,
      studyVersionID,
      participantList,
      hasCaregiverSupport
    } = this.props
    const { offsets, page, hasOrphans, dataSectionSelected } = this.state
    const subsequentOffsets = offsets ? offsets.slice(1) : []
    const isPtpPage = page === 'participants'
    const isCaregiversPage = page === 'caregivers' && hasCaregiverSupport
    const isCaregiverAssignment = page === 'caregiversAssignment' && hasCaregiverSupport
    const canAdd = canWrite || canAddCohort || canAddPtp

    const renderListContent = () => {
      if (isPtpPage) {
        return (
          <ParticipantList
            {...this.props}
            hasOrphans={hasOrphans}
            dataSectionSelected={dataSectionSelected}
          />
        );
      }

      if (isCaregiversPage) {
        return (
          <CaregiversList
            {...this.props}
            hasOrphans={hasOrphans}
            dataSectionSelected={dataSectionSelected}
          />
        );
      }

      if (isCaregiverAssignment) {
        return (
          <CaregiversAssignmentContainer {...this.props} />
        );
      }

      return (
        <CohortList
          offsets={subsequentOffsets}
          list={cohortList || []}
          studyID={studyID}
          studyVersionID={studyVersionID}
          studyLock={studyLock}
        />
      );
    };

    return (
      <div className='participants page'>
        <div className='selected-button-list'>
          <Button
            id='participants'
            selected={page === 'participants'}
            onClick={() => this.togglePage('participants')}
            content='Participants'
          />
          {!noTracksFeature && (
            <Button selected={page === 'tracks'} onClick={() => this.togglePage('tracks')} content='Tracks' />
          )}
          {hasCaregiverSupport && <Button selected={isCaregiversPage} onClick={() => this.togglePage('caregivers')} content='Caregivers' />}
          {hasCaregiverSupport && <Button selected={isCaregiverAssignment} onClick={() => this.togglePage('caregiversAssignment')} content='Caregiver Assignment' />}
        </div>

        <StickyHeader
          className={`ptp-action-buttons-container ${isPtpPage ? '' : 'ptp-action-buttons-tracks-container'}`}
          offset={offsets?.[0] || null}>
          {isPtpPage && <ActiveDataSection {...this.props} onDataSectionSelect={this.onDataSectionSelect} />}
          <div className='ptp-action-buttons'>
            {canMovePtp && !isCaregiverAssignment && !isCaregiversPage && this.renderMovePtpButton()}

            {canAdd && (
              <div>
                {isPtpPage && this.renderPtpButtons()}

                {!isPtpPage && isCaregiversPage && canAddPtp && (
                  <Button
                    id='add-caregiver'
                    content='Add Caregiver'
                    disabled={studyLock}
                    onClick={() =>
                      browserHistory.push(`/studies/${studyID}/${studyVersionID}/participants/caregiver/create`)
                    }
                  />
                )}

                {!isPtpPage && !isCaregiverAssignment && !isCaregiversPage && canAddCohort && !disableTrackCreation && !isReadOnlyState && (
                  <Button
                    id='create-track'
                    content='Create Track'
                    disabled={studyLock}
                    onClick={() =>
                      browserHistory.push(`/studies/${studyID}/${studyVersionID}/participants/tracks/create`)
                    }
                  />
                )}
              </div>
            )}
          </div>
        </StickyHeader>
        <div className='ptp-filters'>
          {isPtpPage && (
            <>
              <Button
                id='download-participant-list'
                className='data-download'
                content='Participants List'
                icon='fas fa-cloud-download-alt'
                isThrottled
                link
                onClick={() => downloadData(studyID, siteID, 'participants')}
              />
              <FilterTagsPopup
                clearStateFilters={this.clearStateFilters}
                customTags={{
                  checkbox: {
                    title: 'Participant status',
                  },
                }}
              />
            </>
          )}
        </div>
        {renderListContent()}
      </div>
    )
  }
}

export default ParticipantsPage

ParticipantsPage.propTypes = {
  canAddPtp: PropTypes.bool,
  canAddCohort: PropTypes.bool,
  canDelete: PropTypes.bool,
  canMovePtp: PropTypes.bool,
  canSeeOrphans: PropTypes.bool,
  canWrite: PropTypes.bool,
  checkedPtps: PropTypes.object,
  clearFilters: PropTypes.func,
  cohortList: PropTypes.arrayOf(PropTypes.array),
  deleteParticipants: PropTypes.func,
  isCaseManager: PropTypes.bool,
  leafSites: PropTypes.arrayOf(PropTypes.number),
  noTracksFeature: PropTypes.bool,
  onSetOrphanedParticipants: PropTypes.func,
  openModal: PropTypes.func,
  page: PropTypes.string,
  participantList: PropTypes.arrayOf(PropTypes.array),
  participantType: PropTypes.string,
  ptpMoveLoading: PropTypes.bool,
  resetCheckedPtps: PropTypes.func,
  siteID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  studyID: PropTypes.number,
  toggleParticipantType: PropTypes.func,
  updateSearchTerm: PropTypes.func,
  hasPiiRole: PropTypes.bool,
  studyLock: PropTypes.bool,
  filterProps: PropTypes.object,
}
